import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Skull } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Premium membership card — real-card look, NO avatar/photo.
 * Inspired by Amex Centurion / Apple Card aesthetics.
 * Front: wordmark, EMV-style chip, large embossed member number (mono),
 *        member name, "MEMBER SINCE", rank in foil.
 * Back:  magnetic-stripe band + QR code + signature panel + small print.
 */

const TIER_BY_RANK = {
  Rookie: {
    base: '#1a1311',       // warm graphite
    base2: '#100a09',
    rim: 'rgba(220, 38, 38, 0.35)',
    chipBase: '#3a2a18',
    chipHilite: '#7a5a2a',
    foil: '#9CA3AF',       // muted steel
    text: '#E8DDD6',
    subtext: '#8E827D',
    accent: '#DC2626',
    label: 'ROOKIE',
  },
  Regular: {
    base: '#1f1413',
    base2: '#100a09',
    rim: 'rgba(220, 38, 38, 0.55)',
    chipBase: '#4a2e1c',
    chipHilite: '#A6802F',
    foil: '#D9C089',       // ember
    text: '#F5EBE7',
    subtext: '#A89F9C',
    accent: '#DC2626',
    label: 'REGULAR',
  },
  Veteran: {
    base: '#241312',
    base2: '#120808',
    rim: 'rgba(224, 200, 149, 0.45)',
    chipBase: '#5a3a1f',
    chipHilite: '#CBA255',
    foil: '#E0C895',       // brighter ember
    text: '#FAF1EA',
    subtext: '#B3A99F',
    accent: '#E0C895',
    label: 'VETERAN',
  },
  Legend: {
    base: '#0c0a09',
    base2: '#000000',
    rim: 'rgba(224, 200, 149, 0.75)',
    chipBase: '#6a4924',
    chipHilite: '#E6CB87',
    foil: '#F2D89C',       // signature gold
    text: '#FFFFFF',
    subtext: '#C8BBA9',
    accent: '#F2D89C',
    label: 'LEGEND',
  },
};

const formatNumber = (memberNo) => {
  // Convert "VC-MAF-000123" → "VC ·· MAF ·· 0001 23" feel like a real card number
  const clean = (memberNo || '').replace(/[^A-Z0-9]/gi, '');
  // Split into chunks of 4 for readability
  return clean.replace(/(.{4})/g, '$1 ').trim();
};

const formatDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const y = String(d.getFullYear()).slice(2);
  return `${m}/${y}`;
};

export function MafiaMemberCard({ user, profileUrl }) {
  const [flipped, setFlipped] = useState(false);
  const t = TIER_BY_RANK[user.rank] || TIER_BY_RANK.Rookie;

  const saveAsImage = async () => {
    try {
      const el = document.querySelector('[data-testid="mafia-card-front"]');
      if (!el) return;
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(el, { backgroundColor: null, scale: 3 });
      const link = document.createElement('a');
      link.download = `mafia-salon-${user.member_no}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Card saved');
    } catch {
      toast.error('Could not save card. Take a screenshot instead.');
    }
  };

  // Shared face style — premium plate
  const faceStyle = {
    background: `
      radial-gradient(circle at 20% 0%, ${t.foil}10 0%, transparent 50%),
      radial-gradient(circle at 80% 100%, ${t.accent}18 0%, transparent 55%),
      linear-gradient(135deg, ${t.base} 0%, ${t.base2} 50%, ${t.base} 100%)
    `,
    border: `1px solid ${t.rim}`,
    borderRadius: '16px',
    boxShadow: `
      inset 0 1px 0 rgba(255,255,255,0.08),
      inset 0 -1px 0 rgba(0,0,0,0.6),
      0 20px 50px rgba(0,0,0,0.55),
      0 0 60px ${t.accent}12
    `,
    color: t.text,
  };

  return (
    <div className="w-full max-w-lg mx-auto" data-testid="mafia-card-wrap">
      <div
        className="card-flip-container w-full cursor-pointer select-none"
        style={{ aspectRatio: '1.586 / 1' }}
        onClick={() => setFlipped((v) => !v)}
        data-testid="mafia-card-flip"
      >
        <div className={`card-flip-inner ${flipped ? 'flipped' : ''}`}>
          {/* ============== FRONT ============== */}
          <div
            className="card-flip-front relative overflow-hidden p-6 sm:p-7 flex flex-col justify-between"
            style={faceStyle}
            data-testid="mafia-card-front"
          >
            {/* subtle guilloché diagonal pattern */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `repeating-linear-gradient(115deg, transparent 0px, transparent 8px, ${t.foil}05 8px, ${t.foil}05 9px)`,
                mixBlendMode: 'overlay',
                opacity: 0.6,
              }}
            />

            {/* Top row: wordmark + rank foil */}
            <div className="relative flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div
                  className="w-7 h-7 flex items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${t.accent}30, transparent)`,
                    border: `1px solid ${t.accent}80`,
                    borderRadius: '4px',
                  }}
                >
                  <Skull size={12} style={{ color: t.accent }} />
                </div>
                <div className="leading-tight">
                  <p className="font-serif tracking-[0.22em] text-[11px] font-bold" style={{ color: t.text, fontFamily: 'Oswald, sans-serif' }}>
                    MAFIA · SALON
                  </p>
                  <p className="text-[9px] tracking-[0.3em]" style={{ color: t.subtext }}>
                    VANGARD CLUB
                  </p>
                </div>
              </div>

              {/* Foil rank label */}
              <div className="text-right">
                <p
                  className="font-serif text-[11px] font-bold tracking-[0.3em]"
                  style={{
                    fontFamily: 'Oswald, sans-serif',
                    background: `linear-gradient(180deg, ${t.foil} 0%, ${t.foil}90 50%, ${t.foil} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t.label}
                </p>
                <p className="text-[9px] tracking-[0.3em] mt-0.5" style={{ color: t.subtext }}>
                  MEMBER
                </p>
              </div>
            </div>

            {/* Middle: chip + member number */}
            <div className="relative flex flex-col gap-4">
              {/* EMV-style chip */}
              <div
                className="w-11 h-8 relative"
                style={{
                  background: `linear-gradient(135deg, ${t.chipHilite} 0%, ${t.chipBase} 50%, ${t.chipHilite} 100%)`,
                  borderRadius: '5px',
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.5)`,
                }}
                aria-hidden
              >
                {/* chip grid pattern */}
                <div
                  className="absolute inset-1.5"
                  style={{
                    background: `
                      linear-gradient(${t.chipBase} 0 0) center/100% 1px no-repeat,
                      linear-gradient(${t.chipBase} 0 0) center/100% 1px no-repeat,
                      linear-gradient(90deg, ${t.chipBase} 0 0) center/1px 100% no-repeat
                    `,
                    backgroundPosition: 'top, bottom, center',
                  }}
                />
              </div>

              {/* Embossed member number */}
              <p
                data-testid="mafia-card-number"
                className="font-mono tabular-nums tracking-[0.18em] text-lg sm:text-xl"
                style={{
                  color: t.text,
                  textShadow: `0 1px 0 rgba(255,255,255,0.12), 0 -1px 0 rgba(0,0,0,0.5)`,
                  letterSpacing: '0.15em',
                }}
              >
                {formatNumber(user.member_no)}
              </p>
            </div>

            {/* Bottom row */}
            <div className="relative flex items-end justify-between gap-4">
              <div>
                <p className="text-[8px] tracking-[0.3em] mb-1" style={{ color: t.subtext }}>
                  MEMBER SINCE
                </p>
                <p
                  className="font-serif text-base font-semibold tracking-wider"
                  style={{ fontFamily: 'Oswald, sans-serif', color: t.text }}
                >
                  {formatDate(user.joined_at)}
                </p>
              </div>
              <div className="text-right min-w-0 max-w-[60%]">
                <p className="text-[8px] tracking-[0.3em] mb-1" style={{ color: t.subtext }}>
                  CARDHOLDER
                </p>
                <p
                  className="font-serif text-base font-bold tracking-wider uppercase truncate"
                  style={{ fontFamily: 'Oswald, sans-serif', color: t.text }}
                >
                  {user.name}
                </p>
              </div>
            </div>
          </div>

          {/* ============== BACK ============== */}
          <div
            className="card-flip-back relative overflow-hidden p-6 sm:p-7 flex flex-col"
            style={faceStyle}
            data-testid="mafia-card-back"
          >
            {/* Magnetic stripe band */}
            <div
              aria-hidden
              className="absolute left-0 right-0 top-6 h-9"
              style={{ background: 'linear-gradient(180deg, #000 0%, #1a1413 50%, #000 100%)' }}
            />

            {/* Subtle pattern */}
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `repeating-linear-gradient(115deg, transparent 0px, transparent 8px, ${t.foil}05 8px, ${t.foil}05 9px)`,
                mixBlendMode: 'overlay',
                opacity: 0.4,
              }}
            />

            {/* Top spacer for stripe */}
            <div className="h-12 shrink-0" />

            <div className="relative flex-1 flex items-center justify-between gap-6">
              {/* Signature panel + small print */}
              <div className="flex-1 min-w-0 flex flex-col gap-3">
                <div
                  className="h-8 flex items-center px-3"
                  style={{
                    background: '#F5EBE7',
                    color: '#0C0A09',
                    fontFamily: 'cursive',
                    fontSize: '14px',
                    borderRadius: '2px',
                  }}
                >
                  <span className="italic opacity-80 truncate">{user.name}</span>
                </div>
                <div>
                  <p className="text-[8px] tracking-[0.3em] mb-1" style={{ color: t.subtext }}>
                    CARD HOLDER SIGNATURE
                  </p>
                  <p className="text-[10px] leading-relaxed" style={{ color: t.subtext }}>
                    Property of Vangard Club. This card grants entry to Mafia Salon games
                    and tracks the cardholder's match record. Non-transferable.
                  </p>
                </div>
              </div>

              {/* QR */}
              <div
                className="shrink-0 p-2.5"
                style={{
                  background: '#F5EBE7',
                  borderRadius: '6px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}
              >
                <QRCodeSVG
                  value={profileUrl || ''}
                  size={88}
                  level="H"
                  bgColor="#F5EBE7"
                  fgColor="#0C0A09"
                  data-testid="mafia-card-qr"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="relative flex items-end justify-between mt-3 shrink-0">
              <p
                className="font-mono tabular-nums tracking-[0.15em] text-[10px]"
                style={{ color: t.subtext }}
              >
                {user.member_no}
              </p>
              <p className="text-[8px] tracking-[0.3em]" style={{ color: t.subtext }}>
                vangardclub.com / mafia
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
        <button
          onClick={saveAsImage}
          data-testid="mafia-card-save-btn"
          className="ms-btn ms-btn-primary"
        >
          <Download size={13} /> Save as Image
        </button>
        <button
          onClick={() => setFlipped((v) => !v)}
          data-testid="mafia-card-flip-btn"
          className="ms-btn ms-btn-secondary"
        >
          {flipped ? 'Show Front' : 'Show QR'}
        </button>
      </div>

      <p className="text-center text-xs ms-text-3 mt-3">Tap the card to flip.</p>
    </div>
  );
}
